import { Injectable, Inject } from '@angular/core';
import { Observable, of, tap, catchError, map, mergeMap } from 'rxjs';
import {
    PBS,
    PersonalBilling,
    ECIM,
    enterpriseCIMServiceToken,
    EnterpriseCIMService,
    isbExperienceServiceToken,
    IsbExperienceService,
    IsbExperience,
    PersonalBillingRequestParams,
    PersonalBillingHttpClient,
    IsbCalculateDownPaymentRequestParams
} from '@nationwide/dgs-angular-billing-common';
import { SessionService } from '../../../../../shared/session/session.service';
import { PaymentPreferencesStateService } from '../payment-preferences-state/payment-preferences-state.service';
import { HttpCachingService } from '../../../../../shared/service/http-caching-service/http-caching.service';
import { SmartMilesService } from '../../../../../shared/service/smart-miles-service/smart-miles.service';
import { SmartMilesEnrollmentResponse } from '../../../../../shared/service/smart-miles-service/smart-miles.model';

@Injectable()
export class PaymentPreferencesApiHttpService {

    private readonly SMART_MILES_LINE_OF_BUSINESS = 'Personal Auto';
    private readonly SMART_MILES_SYSTEM = 'PolicyCenter';
    private readonly SMART_MILES_INELIGIBLE_STATES = ['AK', 'HI', 'LA', 'NC'];

    constructor(
        private session: SessionService,
        private paymentPreferencesState: PaymentPreferencesStateService,
        @Inject(enterpriseCIMServiceToken) private ecimService: EnterpriseCIMService,
        @Inject(isbExperienceServiceToken) private isbExperience: IsbExperienceService,
        private httpCachingService: HttpCachingService,
        private personalBilling: PersonalBillingHttpClient,
        private smartMilesService: SmartMilesService
    ) { }

    fetchRetrieveCustomerAgreementResponse(): Observable<PBS.RetriveCustomerAgreementResponse> {
        return this.httpCachingService.fetchRetrieveCustomerAgreementResponse();
    }

    fetchSavedPaymentMethods(): Observable<IsbExperience.SavedPaymentMethod[]> {
        return this.isbExperience.getSavedPaymentMethods({
            billingSystem: this.session.billingSystem,
            accessToken: this.session.accessToken,
            ecn: this.session.ecn,
            billingAccountNumber: this.session.billingAccountNumber
        }).pipe(
            tap((response) => this.paymentPreferencesState.savedPaymentMethods = response),
            catchError(() => of(null))
        );
    }

    fetchSingleSavedPaymentMethod(methodID: string): Observable<IsbExperience.SavedPaymentMethod> {
        return this.isbExperience.getSingleSavedPaymentMethod({
            billingSystem: this.session.billingSystem,
            methodID,
            accessToken: this.session.accessToken,
            ecn: this.session.ecn,
            billingAccountNumber: this.session.billingAccountNumber
        }).pipe(
            tap((result) => this.paymentPreferencesState.updateSingleSavedPaymentMethod(result)),
            catchError(() => of(null))
        );
    }

    fetchAutomaticPayments(): Observable<IsbExperience.GetPayPlanResponse> {
        return this.isbExperience.getPayPlan({
            accessToken: this.session.accessToken,
            billingSystem: this.session.billingSystem,
            agreementNumber: this.session.billingAccountNumber,
            requestType: 'billingMethod'
        }).pipe(
            tap((response) => this.paymentPreferencesState.automaticPayments = response),
            catchError(() => of(null))
        );
    }

    fetchPaymentHistory(): Observable<IsbExperience.RetrievePaymentsResponse> {
        return this.isbExperience.getPayments({
            accessToken: this.session.accessToken,
            accountNumber: this.session.billingAccountNumber,
            billingSystem: this.session.billingSystem,
            sessionId: this.session.a2aSessionId
        }).pipe(
            tap((response) => this.paymentPreferencesState.paymentsHistory = response),
            catchError(() => of(null))
        );
    }

    fetchSavedRefundMethod(): Observable<PersonalBilling.RefundMethod> {
        return this.isbExperience.getPayPlan({
            accessToken: this.session.accessToken,
            billingSystem: this.session.billingSystem,
            agreementNumber: this.session.billingAccountNumber,
            requestType: 'refundMethod'
        }).pipe(
            map((response) => response.refundMethod),
            tap((response) => this.paymentPreferencesState.refundMethod = <any>response),
            catchError(() => of(null))
        );
    }

    fetchInternetRegistrationResponse(): Observable<ECIM.EnterpriseCustomerInformationManagementResponse> {
        return this.ecimService.getInternetRegistrations({
            accessToken: this.session.accessToken,
            guid: this.session.guid
        }).pipe(
            tap((response) => this.paymentPreferencesState.internetRegistrationResponse = response),
            catchError(() => of(null))
        );
    }

    getAgreement(): Observable<ECIM.Agreement> {
        return this.ecimService.getAgreement({
            accessToken: this.session.accessToken,
            guid: this.session.guid,
            accountNumber: this.session.billingAccountNumber,
            sourceSystem: this.session.sourceSystem
        }).pipe(
            tap((response) => this.paymentPreferencesState.agreementResponse = <any>response),
            catchError(() => of(null))
        );
    }

    retrieveEligiblePayPlan(): Observable<PersonalBilling.EligiblePayPlan> {
        return this.personalBilling.retrieveEligiblePayPlan({
            accessToken: this.session.accessToken,
            accountNumber: this.session.billingAccountNumber
        }).pipe(
            tap((response) => this.paymentPreferencesState.eligiblePayPlan = response),
            catchError(() => of(null))
        );
    }

    calculateDownPayment(calculateDownPaymentRequestParams: IsbCalculateDownPaymentRequestParams): Observable<IsbExperience.CalculateDownPaymentResponse> {
        return this.isbExperience.calculateDownPayment(calculateDownPaymentRequestParams)
            .pipe(
                tap((response) => this.paymentPreferencesState.calculateDownPayment = response),
                catchError(() => of(null))
            );
    }

    fetchBills(): Observable<IsbExperience.Bill[]> {
        return this.isbExperience.getBills({
            agreementNumber: this.session.billingAccountNumber,
            accessToken: this.session.accessToken,
            billingSystem: this.session.billingSystem,
            query: {
                nbrNextBills: 1
            }
        }).pipe(
            map((billsResponse: IsbExperience.GetBillsResponse) => {
                const bills = billsResponse?.[0]?.bills;
                this.paymentPreferencesState.bills = bills;
                return bills;
            }),
            catchError(() => of(null))
        );
    }

    fetchRelatedPolicies(personalBillingRequestParams: PersonalBillingRequestParams): Observable<PersonalBilling.Policy[]> {
        return this.personalBilling.getRelatedPolicies(personalBillingRequestParams).pipe(
            map((policies) => {
                this.paymentPreferencesState.relatedPolicies = policies.relatedPolicies;
                return policies.relatedPolicies;
            }),
            catchError(() => of(null))
        );
    }

    fetchSmartMilesEnrollment(): Observable<SmartMilesEnrollmentResponse[]> {
        return this.httpCachingService.fetchRetrieveCustomerAgreementResponse().pipe(
            mergeMap((rcaResponse) => this.smartMilesService.getEnrolledVehicles(this.extractSmartMilesPolicyNumbers(rcaResponse))),
            tap((response) => this.paymentPreferencesState.smartMilesEnrollment = response),
            catchError(() => of([]))
        );
    }

    private extractSmartMilesPolicyNumbers(rcaResponse: PBS.RetriveCustomerAgreementResponse): string[] {
        const agreements = rcaResponse?.retrieveCustomerAgreementResponse?.return?.profile?.agreementGroup?.agreement || [];
        return agreements
            .filter((agreement) => {
                const placeOfIssue = agreement.placeOfIssue?.toUpperCase();
                return agreement.lineOfBusiness === this.SMART_MILES_LINE_OF_BUSINESS &&
                    agreement.system === this.SMART_MILES_SYSTEM &&
                    (!!placeOfIssue && !this.SMART_MILES_INELIGIBLE_STATES.includes(placeOfIssue));
            })
            .map((agreement) => agreement.agreementNumber);
    }
    fetchZeroAmountDueBalances(personalBillingRequestParams: PersonalBillingRequestParams): Observable<PersonalBilling.ZeroAmountDueApiResponse> {
        return this.personalBilling.getZeroAmountDueValues(personalBillingRequestParams).pipe(
            tap((response) => this.paymentPreferencesState.zeroAmountDueBalances = response),
            catchError(() => of(null))
        );
    }
}
